// src/features/bom/pages/ProductionFloorsPage.tsx

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import {
    getProductionFloors, deactivateProductionFloor,
    ProductionFloor,
} from '../services/productionFloorApi';
import {
    Plus, Search, X, Factory, ChefHat, Beaker, Utensils,
    Wine, Warehouse, ArrowRight, Trash2, RefreshCw, Layers,
    Package, AlertTriangle, Pencil, Eye, MapPin, User,
    MoreVertical, CheckCircle, Clock, Wrench,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// CONSTANTS
// ============================================================

const FLOOR_ICONS: Record<string, React.ElementType> = {
    kitchen: ChefHat, bakery: Beaker, butchery: Utensils,
    bar: Wine, general: Warehouse,
};

const FLOOR_COLORS: Record<string, string> = {
    kitchen: 'from-amber-500 to-orange-600',
    bakery: 'from-rose-400 to-pink-600',
    butchery: 'from-red-500 to-rose-600',
    bar: 'from-purple-500 to-indigo-600',
    general: 'from-slate-500 to-slate-700',
};

const FLOOR_BG_COLORS: Record<string, string> = {
    kitchen: 'bg-amber-50 border-amber-200',
    bakery: 'bg-rose-50 border-rose-200',
    butchery: 'bg-red-50 border-red-200',
    bar: 'bg-purple-50 border-purple-200',
    general: 'bg-slate-50 border-slate-200',
};

const STATUS_CONFIG: Record<string, { icon: React.ElementType; color: string; label: string }> = {
    active: { icon: CheckCircle, color: 'text-emerald-500', label: 'Active' },
    inactive: { icon: X, color: 'text-rose-500', label: 'Inactive' },
    maintenance: { icon: Wrench, color: 'text-amber-500', label: 'Maintenance' },
};

// ============================================================
// SKELETON LOADER
// ============================================================

function CardSkeleton() {
    return (
        <div className="bg-white rounded-2xl border-2 border-slate-200 p-5 animate-pulse">
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-200" />
                    <div className="space-y-2">
                        <div className="h-4 w-24 bg-slate-200 rounded" />
                        <div className="h-3 w-16 bg-slate-100 rounded" />
                    </div>
                </div>
                <div className="h-5 w-16 bg-slate-200 rounded-full" />
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="h-16 bg-slate-100 rounded-lg" />
                <div className="h-16 bg-slate-100 rounded-lg" />
                <div className="h-16 bg-slate-100 rounded-lg" />
            </div>
            <div className="h-3 w-32 bg-slate-100 rounded mb-3" />
            <div className="pt-3 border-t border-slate-100 flex justify-between">
                <div className="h-3 w-20 bg-slate-100 rounded" />
                <div className="flex gap-1">
                    <div className="w-7 h-7 bg-slate-100 rounded-lg" />
                    <div className="w-7 h-7 bg-slate-100 rounded-lg" />
                    <div className="w-7 h-7 bg-slate-100 rounded-lg" />
                </div>
            </div>
        </div>
    );
}

// ============================================================
// DELETE CONFIRMATION MODAL
// ============================================================

function DeactivateConfirmModal({
    floor,
    isOpen,
    onClose,
    onConfirm,
    isLoading,
}: {
    floor: ProductionFloor | null;
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading: boolean;
}) {
    return (
        <AnimatePresence>
            {isOpen && floor && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
                            <AlertTriangle size={32} className="text-rose-500" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900">Deactivate Floor?</h3>
                        <p className="text-sm text-slate-500 mt-2">
                            <strong>{floor.name}</strong> will no longer be available for production operations.
                            This can be reversed later.
                        </p>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={onClose}
                                className="flex-1 px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50"
                                type="button"
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onConfirm}
                                disabled={isLoading}
                                className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold hover:bg-rose-600 disabled:opacity-50 flex items-center justify-center gap-2"
                                type="button"
                            >
                                {isLoading ? <Spinner size="xs" variant="white" /> : <Trash2 size={16} />}
                                Deactivate
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// ============================================================
// QUICK STAT COMPONENT
// ============================================================

function QuickStat({ label, value, icon: Icon, gradient }: {
    label: string; value: number; icon: React.ElementType; gradient: string;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -2 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-all"
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{value.toLocaleString()}</p>
                </div>
                <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
            </div>
        </motion.div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ProductionFloorsPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [confirmDelete, setConfirmDelete] = useState<ProductionFloor | null>(null);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    const { data: floors, isLoading, isFetching, refetch } = useQuery({
        queryKey: ['production-floors', user?.tenantId],
        queryFn: () => getProductionFloors(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const deactivateMutation = useMutation({
        mutationFn: (floorId: string) => deactivateProductionFloor(user!.tenantId, floorId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['production-floors'] });
            addToast({ type: 'success', title: 'Floor deactivated' });
            setConfirmDelete(null);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.message }),
    });

    const floorsList = floors || [];
    const activeFloors = floorsList.filter(f => f.isActive);
    const inactiveFloors = floorsList.filter(f => !f.isActive);

    const filteredFloors = floorsList.filter(f => {
        if (statusFilter === 'active' && !f.isActive) return false;
        if (statusFilter === 'inactive' && f.isActive) return false;
        if (statusFilter === 'maintenance' && f.status !== 'maintenance') return false;
        if (typeFilter !== 'all' && f.floorType !== typeFilter) return false;
        if (search) {
            const q = search.toLowerCase();
            return (
                f.name.toLowerCase().includes(q) ||
                (f.branchName || '').toLowerCase().includes(q) ||
                (f.manager || '').toLowerCase().includes(q)
            );
        }
        return true;
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className="p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg shadow-amber-500/25">
                                <Factory className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">Production Floors</h1>
                                <p className="text-sm text-slate-500">
                                    {floorsList.length} floors · {activeFloors.length} active · {inactiveFloors.length} inactive
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                onClick={() => refetch()}
                                disabled={isFetching}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium transition-all"
                                type="button"
                            >
                                <RefreshCw className={clsx('w-4 h-4', isFetching && 'animate-spin')} /> Refresh
                            </button>
                            <button
                                onClick={() => navigate('/bom/production-floors/new')}
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-amber-500/25 text-sm hover:from-amber-600 hover:to-orange-700 transition-all"
                                type="button"
                            >
                                <Plus className="w-4 h-4" /> Add Floor
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    <QuickStat label="Total Floors" value={floorsList.length} icon={Factory} gradient="from-slate-500 to-slate-700" />
                    <QuickStat label="Active" value={activeFloors.length} icon={CheckCircle} gradient="from-emerald-500 to-teal-600" />
                    <QuickStat label="Inactive" value={inactiveFloors.length} icon={AlertTriangle} gradient="from-rose-400 to-rose-600" />
                    <QuickStat label="Maintenance" value={floorsList.filter(f => f.status === 'maintenance').length} icon={Wrench} gradient="from-amber-500 to-orange-600" />
                    <QuickStat label="Recipes" value={floorsList.reduce((s, f) => s + (f.recipeCount || 0), 0)} icon={Layers} gradient="from-purple-500 to-indigo-600" />
                </div>

                {/* Search & Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <div className="p-3 flex flex-col lg:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search floors by name, branch, or manager..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 focus:bg-white transition-all"
                            />
                            {search && (
                                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600" type="button">
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                                {[
                                    { key: 'all', label: 'All Status' },
                                    { key: 'active', label: 'Active' },
                                    { key: 'inactive', label: 'Inactive' },
                                    { key: 'maintenance', label: 'Maintenance' },
                                ].map(f => (
                                    <button
                                        key={f.key}
                                        onClick={() => setStatusFilter(f.key)}
                                        className={clsx(
                                            'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap',
                                            statusFilter === f.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                        )}
                                        type="button"
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white text-slate-600"
                            >
                                <option value="all">All Types</option>
                                <option value="kitchen">Kitchen</option>
                                <option value="bakery">Bakery</option>
                                <option value="butchery">Butchery</option>
                                <option value="bar">Bar</option>
                                <option value="general">General</option>
                            </select>
                            {(search || statusFilter !== 'all' || typeFilter !== 'all') && (
                                <button
                                    onClick={() => { setSearch(''); setStatusFilter('all'); setTypeFilter('all'); }}
                                    className="text-xs text-amber-600 font-medium hover:text-amber-700 px-2"
                                    type="button"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Results count */}
                <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">
                        Showing <strong className="text-slate-700">{filteredFloors.length}</strong> floor{filteredFloors.length !== 1 ? 's' : ''}
                        {(search || statusFilter !== 'all' || typeFilter !== 'all') && (
                            <span className="text-slate-400"> (filtered from {floorsList.length})</span>
                        )}
                    </p>
                </div>

                {/* Content */}
                {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
                    </div>
                ) : filteredFloors.length === 0 ? (
                    <EmptyState
                        icon={<Factory className="w-20 h-20" />}
                        title={search || statusFilter !== 'all' || typeFilter !== 'all' ? "No matching floors" : "No Production Floors"}
                        description={
                            search || statusFilter !== 'all' || typeFilter !== 'all'
                                ? "Try adjusting your search or filters."
                                : "Create your first production floor to start managing kitchen, bakery, and bar operations."
                        }
                        action={
                            !search && statusFilter === 'all' && typeFilter === 'all'
                                ? { label: 'Create Your First Floor', onClick: () => navigate('/bom/production-floors/new') }
                                : { label: 'Clear Filters', onClick: () => { setSearch(''); setStatusFilter('all'); setTypeFilter('all'); } }
                        }
                    />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredFloors.map((floor, idx) => {
                            const Icon = FLOOR_ICONS[floor.floorType] || Warehouse;
                            const gradient = FLOOR_COLORS[floor.floorType] || FLOOR_COLORS.general;
                            const statusConfig = STATUS_CONFIG[floor.status] || STATUS_CONFIG.active;
                            const StatusIcon = statusConfig.icon;

                            return (
                                <motion.div
                                    key={floor.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.04 }}
                                    whileHover={{ y: -4 }}
                                    className={clsx(
                                        'group bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden',
                                        floor.isActive
                                            ? 'border-slate-200 hover:border-amber-300 hover:shadow-xl cursor-pointer'
                                            : 'border-rose-200 bg-rose-50/30'
                                    )}
                                >
                                    {/* Colored top bar */}
                                    <div className={`h-1.5 bg-gradient-to-r ${gradient}`} />

                                    <div className="p-5">
                                        {/* Header */}
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center gap-3" onClick={() => navigate(`/bom/production-floors/${floor.id}`)}>
                                                <div className={clsx(
                                                    'w-11 h-11 rounded-xl flex items-center justify-center shadow-md bg-gradient-to-br',
                                                    gradient
                                                )}>
                                                    <Icon size={22} className="text-white" />
                                                </div>
                                                <div className="min-w-0">
                                                    <h3 className="font-semibold text-slate-900 truncate">{floor.name}</h3>
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                        <MapPin size={11} className="text-slate-400 flex-shrink-0" />
                                                        <p className="text-xs text-slate-400 truncate">{floor.branchName || 'Unknown Branch'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <StatusIcon size={14} className={statusConfig.color} />
                                                <span className={clsx('text-xs font-semibold capitalize', statusConfig.color)}>
                                                    {statusConfig.label}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Stats Grid */}
                                        <div className="grid grid-cols-3 gap-2 mb-4" onClick={() => navigate(`/bom/production-floors/${floor.id}`)}>
                                            <div className="text-center p-2.5 bg-slate-50 rounded-xl hover:bg-amber-50 transition-colors">
                                                <p className="text-[10px] text-slate-400 uppercase font-semibold">Recipes</p>
                                                <p className="text-lg font-bold text-slate-700">{floor.recipeCount || 0}</p>
                                            </div>
                                            <div className="text-center p-2.5 bg-slate-50 rounded-xl hover:bg-amber-50 transition-colors">
                                                <p className="text-[10px] text-slate-400 uppercase font-semibold">Stock</p>
                                                <p className="text-lg font-bold text-slate-700">{floor.inventoryCount || 0}</p>
                                            </div>
                                            <div className="text-center p-2.5 bg-slate-50 rounded-xl hover:bg-amber-50 transition-colors">
                                                <p className="text-[10px] text-slate-400 uppercase font-semibold">Batches</p>
                                                <p className="text-lg font-bold text-slate-700">{floor.activeBatchCount || 0}</p>
                                            </div>
                                        </div>

                                        {/* Manager */}
                                        {floor.manager && (
                                            <div className="flex items-center gap-1.5 mb-3" onClick={() => navigate(`/bom/production-floors/${floor.id}`)}>
                                                <User size={12} className="text-slate-400" />
                                                <p className="text-xs text-slate-500 truncate">{floor.manager}</p>
                                            </div>
                                        )}

                                        {/* Footer */}
                                        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                                            <span className="text-[11px] text-slate-400">
                                                {new Date(floor.createdAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </span>
                                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/bom/production-floors/${floor.id}`);
                                                    }}
                                                    className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                                    type="button"
                                                    title="View floor details"
                                                >
                                                    <Eye size={15} />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/bom/production-floors/${floor.id}/edit`);
                                                    }}
                                                    className="p-2 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                                    type="button"
                                                    title="Edit floor"
                                                >
                                                    <Pencil size={15} />
                                                </button>
                                                {floor.isActive && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setConfirmDelete(floor);
                                                        }}
                                                        className="p-2 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
                                                        type="button"
                                                        title="Deactivate floor"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Deactivate Confirmation Modal */}
            <DeactivateConfirmModal
                floor={confirmDelete}
                isOpen={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={() => confirmDelete && deactivateMutation.mutate(confirmDelete.id)}
                isLoading={deactivateMutation.isPending}
            />
        </div>
    );
}